import React from 'react'
// PRO-130: the deep `antd/es/date-picker` subpath, NOT the `antd` root barrel.
import AntdDatePicker from 'antd/es/date-picker'
import type {
  DatePickerProps as AntdDatePickerProps,
  RangePickerProps as AntdRangePickerProps,
} from 'antd/es/date-picker'
import type { Dayjs } from 'dayjs'
import type { PickerRef } from 'rc-picker'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { DatePickerShadcn, RangePickerShadcn } from './DatePicker.shadcn'

export type { PickerMode } from './format'

/**
 * Compat seam leaf for antd's `antd/es/date-picker`, after `../Button` (#970),
 * `../Typography` + `../Space` (#971), `../Tag` (#972), the presentational
 * batch (#975), `../Tooltip` + `../Modal` (#982), `../Spin` (#983), the login
 * surface (#984), `../Select` + `../Dropdown` and `../Menu` (#1006).
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` welds antd's whole component surface onto whatever
 * chunk imports it.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch each
 * dispatcher below renders antd's own component with the props and the ref it
 * was given: same element, same class names, same DOM, same imperative handle.
 *
 * `src/antd` has always RE-EXPORTED DatePicker rather than shadowing it, so
 * this is the ORDINARY kind of port (like `../Input`, `../Card`): parity is
 * against `antd/es/date-picker` itself, not against a previous seam wrapper.
 * `../__tests__/datepicker-seam-parity.test.tsx` asserts that node for node
 * across a prop matrix and is the test to keep green.
 *
 * ---------------------------------------------------------------------------
 * THE STATICS
 *
 * antd hangs six pickers off `DatePicker`, and all six dispatch here.
 * `MonthPicker`/`WeekPicker`/`YearPicker`/`QuarterPicker`/`TimePicker` are
 * antd's own `picker="…"` presets, so each is built off THIS dispatcher rather
 * than off either half — `<DatePicker.MonthPicker>` therefore follows the
 * toggle exactly as `<DatePicker picker="month">` does, and the seam cannot end
 * up half-switched. Dropping one of them is the recurring seam bug (AGENTS.md),
 * and this repo really does reach for `.RangePicker` (16 tags) — the rest have
 * no call site but stay because narrowing the export is a breaking change
 * nobody asked for.
 *
 * antd's two `_InternalPanel…DoNotUseOrYouWillBeFired` escape hatches and
 * `generatePicker` are deliberately absent: nothing in this repo names them,
 * and a shadcn half for antd's internal panel factory would be a surface with
 * no consumer.
 *
 * `TimePicker` — antd's SEPARATE top-level component, not `DatePicker.
 * TimePicker` — is NOT ported and is not shadowed by the barrel, so its 5 call
 * sites stay on antd in both modes. A screen carrying one is therefore MIXED
 * with the toggle on, like every other switched screen. Filed as a follow-up.
 *
 * ---------------------------------------------------------------------------
 * THE TYPES STAY antd's
 *
 * `DatePickerProps` / `RangePickerProps` are imported at ~45 call sites and at
 * every wrapper on top of this control (`WhDatePicker`, `DatePickerBase`,
 * `DatePickerSimpleString`, `FilterMenuDatePicker`). They are re-exported below
 * as antd's own types WIDENED by one optional prop, never narrowed — so this
 * stays a reskin instead of a call-site migration. `update-design` replaces
 * them with the seam's own declarations, which it can afford to because antd is
 * deleted there; here that would break the wrappers.
 *
 * The one added prop is `showWeekNumber`. It is rc-picker's, and antd never
 * surfaced it — which is why the house week-number column has always been a
 * `dateRender` hack. `src/storybook/components/DatePickerBase` keeps that hack
 * for the antd branch and passes this prop for the shadcn one; read it before
 * touching either.
 *
 * NOT ported, and named rather than left to be discovered: `multiple`
 * (antd 5.14's multi-value mode — 0 call sites), `presets`/`ranges` (0 call
 * sites), `showTime.use12Hours` (0 call sites — the input MASK honours it,
 * but the time panel renders one 0–23 hour column and no AM/PM column, so a
 * 12-hour panel is scope this port does not take), and antd's `panelRender`
 * for the internal panel factory. Each is
 * dropped from the DOM in the shadcn half rather than forwarded, so none can
 * leak onto a `<div>`.
 * ------------------------------------------------------------------------- */

/** The single optional prop the seam adds on top of antd's own types. */
type SeamExtraProps = {
  /**
   * rc-picker's week-number column. Honoured by the shadcn half only: antd
   * 5.15.4's DatePicker has no such prop, so with the toggle OFF the column
   * still comes from `useDatePickerBaseProps`'s `dateRender`.
   */
  showWeekNumber?: boolean
}

export type DatePickerProps<ValueType = Dayjs> =
  AntdDatePickerProps<ValueType> & SeamExtraProps

export type RangePickerProps = AntdRangePickerProps & SeamExtraProps

const DatePickerDispatcher = React.forwardRef<PickerRef, DatePickerProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn')
      return <DatePickerShadcn {...props} ref={ref} />

    return <AntdDatePicker {...(props as AntdDatePickerProps)} ref={ref} />
  }
)

DatePickerDispatcher.displayName = 'DatePicker'

const RangePickerDispatcher = React.forwardRef<PickerRef, RangePickerProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn')
      return <RangePickerShadcn {...props} ref={ref} />

    return (
      <AntdDatePicker.RangePicker
        {...(props as AntdRangePickerProps)}
        ref={ref}
      />
    )
  }
)

RangePickerDispatcher.displayName = 'DatePicker.RangePicker'

/**
 * antd's `picker="…"` presets. Built off the DISPATCHER, so each one switches
 * with the toggle; `picker` is pinned after the caller's props for the same
 * reason antd pins it — these presets `Omit<…, 'picker'>` precisely so a call
 * site cannot override the mode.
 */
const preset = (picker: DatePickerProps['picker'], displayName: string) => {
  const Preset = React.forwardRef<PickerRef, DatePickerProps>((props, ref) => (
    <DatePickerDispatcher {...props} picker={picker} ref={ref} />
  ))
  Preset.displayName = displayName
  return Preset
}

type PresetPicker = React.ForwardRefExoticComponent<
  Omit<DatePickerProps, 'ref' | 'picker'> & React.RefAttributes<PickerRef>
>

/**
 * The shape antd exports: a callable with the sub-pickers hung off it. Kept
 * structurally identical so a call site that already types against antd's
 * `DatePicker` keeps compiling.
 */
type DatePickerSeam = (<ValueType = Dayjs>(
  props: DatePickerProps<ValueType> & React.RefAttributes<PickerRef>
) => React.ReactElement) & {
  displayName?: string
  RangePicker: React.ForwardRefExoticComponent<
    Omit<RangePickerProps, 'ref'> & React.RefAttributes<PickerRef>
  >
  WeekPicker: PresetPicker
  MonthPicker: PresetPicker
  YearPicker: PresetPicker
  QuarterPicker: PresetPicker
  TimePicker: PresetPicker
}

const DatePicker = DatePickerDispatcher as unknown as DatePickerSeam

DatePicker.RangePicker =
  RangePickerDispatcher as unknown as DatePickerSeam['RangePicker']
DatePicker.WeekPicker = preset('week', 'DatePicker.WeekPicker') as PresetPicker
DatePicker.MonthPicker = preset(
  'month',
  'DatePicker.MonthPicker'
) as PresetPicker
DatePicker.YearPicker = preset('year', 'DatePicker.YearPicker') as PresetPicker
DatePicker.QuarterPicker = preset(
  'quarter',
  'DatePicker.QuarterPicker'
) as PresetPicker
DatePicker.TimePicker = preset('time', 'DatePicker.TimePicker') as PresetPicker

export { DatePicker }
export default DatePicker
